import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/pro-light-svg-icons/faChevronLeft';
import { TypedMemo } from '../../../common/types';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { ArtistSwitcher, ArtistSwitcherSheetControl } from '../../../accountSelector/ArtistSwitcher';
import { SMLNavItem } from '../smlNavigation';
import { useMobilePageTitleValue } from '../hooks/useMobilePageTitleValue';
import { smlNavigationChromeMessages as messages } from '../smlNavigationMessages';
import styles from './MobileDeepTopBar.module.scss';

interface MobileDeepTopBarProps {
  /** The destination this page sits below. */
  parent: SMLNavItem;
  /** design: navigation i1. The compact pill opens the switcher sheet. Absent on a Discover page. */
  artistSheet?: ArtistSwitcherSheetControl;
}

/**
 * Design b4: a page below a destination swaps the top bar for back + title. The mono line names
 * the parent destination; the title is the entity the page gave (`useMobilePageTitle`). Back
 * goes one step back in the app, or to the parent when the reader arrived from a link. The bar
 * ends with the compact artist pill, but not on a Discover page (design: navigation i1).
 */
const MobileDeepTopBarComponent: React.FC<MobileDeepTopBarProps> = ({ parent, artistSheet }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const pageTitle = useMobilePageTitleValue();
  const parentLabel = intl.formatMessage(parent.label);
  const goBack = useCallback(() => router.goBack(parent.route), [router, parent.route]);

  return (
    <header className={styles.bar} data-testid="mobile-deep-top-bar">
      <button
        type="button"
        className={styles.back}
        onClick={goBack}
        aria-label={intl.formatMessage(messages.back, { destination: parentLabel })}
      >
        <FontAwesomeIcon icon={faChevronLeft} />
      </button>
      <div className={styles.text}>
        {pageTitle !== undefined && <span className={styles.eyebrow}>{parentLabel}</span>}
        <span className={styles.title}>{pageTitle ?? parentLabel}</span>
      </div>
      {/* design: navigation i1. The compact pill sits at the far right; nothing follows it. */}
      {artistSheet !== undefined && (
        <div className={styles.switcher}>
          <ArtistSwitcher variant="compact" sheet={artistSheet} />
        </div>
      )}
    </header>
  );
};

export const MobileDeepTopBar = TypedMemo(MobileDeepTopBarComponent);
